import { create } from 'zustand';
import type {
  ApprovalEntry,
  ApprovalStatus,
  GovernancePolicy,
  DashboardStats,
  ComplianceMetrics,
} from './types';

// ─── API Helpers ────────────────────────────────────────────────────────────

async function authHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

// ─── Store ──────────────────────────────────────────────────────────────────

interface GovernanceState {
  // Approvals
  approvals: ApprovalEntry[];
  approvalsTotalCount: number;
  approvalsPage: number;
  approvalsPageSize: number;
  approvalsTotalPages: number;
  approvalsStatusFilter: ApprovalStatus | null;
  approvalsLoading: boolean;

  // Policies
  policies: GovernancePolicy[];
  policiesLoading: boolean;

  // Dashboard
  stats: DashboardStats | null;
  compliance: ComplianceMetrics | null;
  dashboardLoading: boolean;

  // Error
  error: string | null;

  // Actions — Approvals
  loadApprovals: (getToken: () => Promise<string | null>) => Promise<void>;
  setApprovalsPage: (page: number) => void;
  setApprovalsStatusFilter: (status: ApprovalStatus | null) => void;
  approveApproval: (id: string, getToken: () => Promise<string | null>) => Promise<void>;
  rejectApproval: (id: string, note: string | undefined, getToken: () => Promise<string | null>) => Promise<void>;
  goLiveApproval: (id: string, getToken: () => Promise<string | null>) => Promise<void>;
  resubmitApproval: (id: string, getToken: () => Promise<string | null>) => Promise<void>;

  // Actions — Policies
  loadPolicies: (getToken: () => Promise<string | null>) => Promise<void>;
  updatePolicy: (toolId: string, data: { toolLabel: string; requiresApproval: boolean; approverRole: string }, getToken: () => Promise<string | null>) => Promise<void>;

  // Actions — Dashboard
  loadDashboard: (getToken: () => Promise<string | null>) => Promise<void>;
}

export const useGovernanceStore = create<GovernanceState>((set, get) => ({
  // ─── Initial State ──────────────────────────────────────────────
  approvals: [],
  approvalsTotalCount: 0,
  approvalsPage: 1,
  approvalsPageSize: 20,
  approvalsTotalPages: 0,
  approvalsStatusFilter: null,
  approvalsLoading: false,

  policies: [],
  policiesLoading: false,

  stats: null,
  compliance: null,
  dashboardLoading: false,
  error: null,

  // ─── Approvals ──────────────────────────────────────────────────

  loadApprovals: async (getToken) => {
    set({ approvalsLoading: true, error: null });
    try {
      const { approvalsPage, approvalsPageSize, approvalsStatusFilter } = get();
      const params = new URLSearchParams();
      params.set('page', String(approvalsPage));
      params.set('pageSize', String(approvalsPageSize));
      if (approvalsStatusFilter) params.set('status', approvalsStatusFilter);

      const headers = await authHeaders(getToken);
      const res = await fetch(`/api/dotnet/approvals?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      set({
        approvals: data.items,
        approvalsTotalCount: data.totalCount,
        approvalsPage: data.page,
        approvalsTotalPages: data.totalPages,
        approvalsLoading: false,
      });
    } catch {
      set({ error: 'Genehmigungen konnten nicht geladen werden.', approvalsLoading: false });
    }
  },

  setApprovalsPage: (page) => set({ approvalsPage: page }),

  setApprovalsStatusFilter: (status) => set({ approvalsStatusFilter: status, approvalsPage: 1 }),

  approveApproval: async (id, getToken) => {
    const headers = await authHeaders(getToken);
    const res = await fetch(`/api/dotnet/approvals/${id}/approve`, { method: 'POST', headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    await get().loadApprovals(getToken);
  },

  rejectApproval: async (id, note, getToken) => {
    const headers = await authHeaders(getToken);
    const res = await fetch(`/api/dotnet/approvals/${id}/reject`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ note: note ?? null }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    await get().loadApprovals(getToken);
  },

  goLiveApproval: async (id, getToken) => {
    const headers = await authHeaders(getToken);
    const res = await fetch(`/api/dotnet/approvals/${id}/go-live`, { method: 'POST', headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    await get().loadApprovals(getToken);
  },

  resubmitApproval: async (id, getToken) => {
    const headers = await authHeaders(getToken);
    const res = await fetch(`/api/dotnet/approvals/${id}/resubmit`, { method: 'POST', headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `HTTP ${res.status}`);
    }
    await get().loadApprovals(getToken);
  },

  // ─── Policies ───────────────────────────────────────────────────

  loadPolicies: async (getToken) => {
    set({ policiesLoading: true, error: null });
    try {
      const headers = await authHeaders(getToken);
      const res = await fetch('/api/dotnet/governance/policies', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      set({ policies: data, policiesLoading: false });
    } catch {
      set({ error: 'Richtlinien konnten nicht geladen werden.', policiesLoading: false });
    }
  },

  updatePolicy: async (toolId, data, getToken) => {
    const headers = await authHeaders(getToken);
    const res = await fetch(`/api/dotnet/governance/policies/${encodeURIComponent(toolId)}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    await get().loadPolicies(getToken);
  },

  // ─── Dashboard ──────────────────────────────────────────────────

  loadDashboard: async (getToken) => {
    set({ dashboardLoading: true, error: null });
    try {
      const headers = await authHeaders(getToken);
      const [statsRes, complianceRes] = await Promise.all([
        fetch('/api/dotnet/audit-logs/stats', { headers }),
        fetch('/api/dotnet/audit-logs/compliance', { headers }),
      ]);

      if (!statsRes.ok || !complianceRes.ok) throw new Error('Dashboard-Daten konnten nicht geladen werden.');

      const stats = await statsRes.json();
      const compliance = await complianceRes.json();
      set({ stats, compliance, dashboardLoading: false });
    } catch {
      set({ error: 'Dashboard-Daten konnten nicht geladen werden.', dashboardLoading: false });
    }
  },
}));
